import { PlaygroundStorage } from '../storage/PlaygroundStorage'
import { Issue, IssueJSON } from './Issue'

let allIssuesQuery: IssuesStoreQuery

export class IssuesStoreQuery {
  constructor(private readonly store: IssuesStore) {}

  static allIssues() {
    if (!allIssuesQuery) {
      allIssuesQuery = new IssuesStoreQuery(IssuesStore.shared())
      IssuesStore.shared().registerQuery(allIssuesQuery)
    }
    return allIssuesQuery
  }

  private issues: Issue[] = []

  getSnapshot() {
    return this.issues
  }

  async reload() {
    this.issues = await this.store.issues()
    this.dispatchChange()
  }

  // Listeners

  private listeners = new Set<() => void>()
  addListener(listener: () => void) {
    this.listeners.add(listener)
  }
  removeListener(listener: () => void) {
    this.listeners.delete(listener)
  }

  dispatchChange() {
    this.listeners.forEach((listener) => listener())
  }
}

let sharedIssuesStore: IssuesStore

export class IssuesStore {
  private storage = new PlaygroundStorage()

  static shared() {
    if (!sharedIssuesStore) {
      sharedIssuesStore = new IssuesStore()
    }
    return sharedIssuesStore
  }

  async issues(): Promise<Issue[]> {
    const database = await this.storage.getDatabase()
    return new Promise<Issue[]>((resolve, reject) => {
      let allIssues: Issue[] = []

      const transaction = database.transaction([PlaygroundStorage.issuesStoreName], 'readonly')
      transaction.onerror = (event) => {
        reject((event.target as IDBRequest).error)
      }
      transaction.oncomplete = (event) => {
        resolve(allIssues)
      }

      const store = transaction.objectStore(PlaygroundStorage.issuesStoreName)

      const request = store.getAll()
      request.onsuccess = (event) => {
        const issuesJSON = (event.target as IDBRequest<IssueJSON[]>).result
        allIssues = issuesJSON.map((issueJSON) => Issue.fromJSON(issueJSON))
      }
    })
  }

  async issuesWithRunID(runID: string): Promise<Issue[]> {
    const database = await this.storage.getDatabase()
    return new Promise<Issue[]>((resolve, reject) => {
      let allIssues: Issue[] = []

      const transaction = database.transaction([PlaygroundStorage.issuesStoreName], 'readonly')
      transaction.onerror = (event) => {
        reject((event.target as IDBRequest).error)
      }
      transaction.oncomplete = (event) => {
        resolve(allIssues)
      }

      const store = transaction.objectStore(PlaygroundStorage.issuesStoreName)
      const runIDIndex = store.index(PlaygroundStorage.issuesRunIDIndexName)

      const request = runIDIndex.getAll(runID)
      request.onsuccess = (event) => {
        const issuesJSON = (event.target as IDBRequest<IssueJSON[]>).result
        allIssues = issuesJSON.map((issueJSON) => Issue.fromJSON(issueJSON))
      }
    })
  }

  async addIssues(issues: Issue[]) {
    const database = await this.storage.getDatabase()
    return new Promise<void>((resolve, reject) => {
      const transaction = database.transaction([PlaygroundStorage.issuesStoreName], 'readwrite')
      transaction.onerror = (event) => {
        reject((event.target as IDBRequest).error)
      }
      transaction.oncomplete = (event) => {
        resolve()
      }

      const store = transaction.objectStore(PlaygroundStorage.issuesStoreName)
      issues.forEach((issue) => store.add(issue.toJSON()))
    }).then(() => {
      this.dispatchChange()
    })
  }

  async clearAllIssues() {
    const database = await this.storage.getDatabase()
    return new Promise<void>((resolve, reject) => {
      const transaction = database.transaction([PlaygroundStorage.issuesStoreName], 'readwrite')
      transaction.onerror = (event) => {
        reject((event.target as IDBRequest).error)
      }
      transaction.oncomplete = (event) => {
        resolve()
      }

      const store = transaction.objectStore(PlaygroundStorage.issuesStoreName)
      const request = store.clear()
      request.onsuccess = (event) => {}
    }).then(() => {
      this.dispatchChange()
    })
  }

  // Queries

  private queries = new Set<IssuesStoreQuery>()
  registerQuery(query: IssuesStoreQuery) {
    this.queries.add(query)
  }
  unregisterQuery(query: IssuesStoreQuery) {
    this.queries.delete(query)
  }
  dispatchChange() {
    this.queries.forEach((query) => query.reload())
  }
}
